import {useState, useEffect, useRef, useCallback} from 'react'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import {isHydrated} from '../utils/utils'

/**
 * Custom hook to manage the sticky call-to-action (CTA) button visibility based on viewport size,
 * user interactions, and zoom level. Primarily used to toggle the sticky CTA on mobile devices
 * when the top cart CTA is not visible.
 *
 * @param {Object} topCartCTARef - A React ref object pointing to the top cart CTA element.
 * @returns {Object} - An object containing:
 *   - {boolean} enableStickyCTA - A flag indicating whether the sticky CTA should be enabled.
 *   - {Function} handleInputFocus - A callback function to handle input focus events, disabling the sticky CTA.
 *   - {Function} handleInputBlur - A callback function to handle input blur events, potentially re-enabling the sticky CTA.
 *
 * @example
 * const { enableStickyCTA, handleInputFocus, handleInputBlur } = useStickyCTA(topCartCTARef);
 *
 * // Use `enableStickyCTA` to conditionally render the sticky CTA.
 * // Attach `handleInputFocus` and `handleInputBlur` to input elements to manage CTA visibility during text input.
 */
const useStickyCTA = (topCartCTARef) => {
    const isDesktop = useBreakpointValue({base: false, lg: true})
    // Ref to store the initial distance between two touch points
    const initialDistance = useRef(null)
    // Ref to track if the user is currently zooming
    const isZooming = useRef(false)
    // State to manage the sticky CTA visibility and its previous state
    const [stickyCTAState, setStickyCTAState] = useState({
        enableStickyCTA: !isDesktop, // Enable sticky CTA by default on non-desktop
        previousEnableStickyCTA: null
    })

    // Function to update the sticky CTA state
    const updateStickyCTA = useCallback((enable) => {
        setStickyCTAState((prevState) => ({
            ...prevState,
            enableStickyCTA: enable
        }))
    }, [])

    // Effect to observe the top cart CTA element and manage sticky CTA visibility
    useEffect(() => {
        const target = topCartCTARef.current
        updateStickyCTA(!isDesktop)

        if (isHydrated() && target && !isDesktop) {
            // Create an IntersectionObserver to observe the visibility of the target element
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        const activeElement = document.activeElement
                        // Check if the focused element is a text input or textarea
                        const isInputFocused =
                            (activeElement.tagName === 'INPUT' && activeElement.type === 'text') ||
                            activeElement.tagName === 'TEXTAREA'
                        // Check if the viewport is zoomed
                        const isZoomed = window.innerWidth !== document.documentElement.clientWidth

                        // Update sticky CTA state based on element visibility, input focus, and zoom
                        updateStickyCTA(!entry.isIntersecting && !isInputFocused && !isZoomed)
                    })
                },
                {
                    root: null,
                    rootMargin: '0px',
                    threshold: 1
                }
            )

            observer.observe(target)

            // Cleanup function to disconnect the observer
            return () => {
                observer.disconnect()
            }
        }
    }, [isDesktop, topCartCTARef.current, updateStickyCTA])

    // Effect to handle touch events for zoom detection
    useEffect(() => {
        const handleTouchStart = (event) => {
            if (event.touches.length === 2) {
                const [touch1, touch2] = event.touches
                // Calculate the initial distance between two touch points
                initialDistance.current = Math.hypot(
                    touch2.pageX - touch1.pageX,
                    touch2.pageY - touch1.pageY
                )
            }
        }

        const handleTouchMove = (event) => {
            if (event.touches.length === 2 && initialDistance.current !== null) {
                const [touch1, touch2] = event.touches
                // Calculate the current distance between two touch points
                const currentDistance = Math.hypot(
                    touch2.pageX - touch1.pageX,
                    touch2.pageY - touch1.pageY
                )

                // If the distance changes, the user is zooming
                if (currentDistance !== initialDistance.current) {
                    isZooming.current = true
                    updateStickyCTA(false)
                }
            }
        }

        const handleTouchEnd = () => {
            initialDistance.current = null
            const currentWidth = window.visualViewport
                ? window.visualViewport.width
                : window.innerWidth
            // If zooming has stopped and the viewport width is back to normal, re-enable the sticky CTA
            if (isZooming.current && currentWidth >= window.screen.width) {
                updateStickyCTA(true)
                isZooming.current = false
            }
        }

        // Add touch event listeners
        window.addEventListener('touchstart', handleTouchStart)
        window.addEventListener('touchmove', handleTouchMove)
        window.addEventListener('touchend', handleTouchEnd)

        // Cleanup function to remove touch event listeners
        return () => {
            window.removeEventListener('touchstart', handleTouchStart)
            window.removeEventListener('touchmove', handleTouchMove)
            window.removeEventListener('touchend', handleTouchEnd)
        }
    }, [updateStickyCTA])

    // Callback to handle input focus events
    const handleInputFocus = useCallback((event) => {
        if (
            (event.target.tagName === 'INPUT' && event.target.type === 'text') ||
            event.target.tagName === 'TEXTAREA'
        ) {
            // Disable sticky CTA when a text input or textarea is focused
            setStickyCTAState((prevState) => ({
                ...prevState,
                previousEnableStickyCTA: prevState.enableStickyCTA,
                enableStickyCTA: false
            }))
        }
    }, [])

    // Callback to handle input blur events
    const handleInputBlur = useCallback((event) => {
        const currentWidth = window.visualViewport ? window.visualViewport.width : window.innerWidth
        if (
            ((event.target.tagName === 'INPUT' && event.target.type === 'text') ||
                event.target.tagName === 'TEXTAREA') &&
            currentWidth === window.screen.width
        ) {
            // Re-enable sticky CTA when a text input or textarea loses focus and the viewport is normal
            setStickyCTAState((prevState) => ({
                ...prevState,
                enableStickyCTA: prevState.previousEnableStickyCTA
            }))
        }
    }, [])

    // Return the sticky CTA state and input event handlers
    return {enableStickyCTA: stickyCTAState.enableStickyCTA, handleInputFocus, handleInputBlur}
}

export default useStickyCTA
